/* ============================================================
 * TEN-FOUR Product Builder — v0.3
 * NB Home Tech
 * ------------------------------------------------------------
 * v0.2: add-ons can be matched to a rack option. When a block
 * declares "Match to rack option", the builder resolves that
 * add-on's variant by option value on every change, so the
 * price follows the selected rack height. Variant IDs are
 * resolved at submit time, not at tick time.
 *
 * Unavailable add-ons are unticked and excluded from the
 * submitted items array, because /cart/add.js with an items
 * array is atomic — one bad line fails the whole call.
 *
 * v0.3: shows a bundle saving in the running total. This is
 * cosmetic. The real discount is a native Buy X Get Y automatic
 * discount configured in Shopify admin, which applies at
 * checkout. Nothing here touches what the customer is charged.
 * ============================================================ */

(function () {
  'use strict';

  const CART_ADD = (window.routes && window.routes.cart_add_url) || '/cart/add';

  /* --- helpers ---------------------------------------------- */
  function normalise(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/\s+/g, '');
  }

  function formatMoney(cents, format) {
    const value = Number(cents) || 0;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    const key = match ? match[1] : 'amount';

    function num(n, decimals, thousands, decimal) {
      const fixed = (n / 100).toFixed(decimals);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    }

    let out;
    switch (key) {
      case 'amount_no_decimals':
        out = num(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        out = num(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = num(value, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        out = num(value, 2, ' ', ',');
        break;
      default:
        out = num(value, 2, ',', '.');
    }
    return format.replace(placeholder, out);
  }

  /* --- element ---------------------------------------------- */
  class TenFourBuilder extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.designMode = this.dataset.designMode === 'true';

      const raw = this.querySelector('[data-tf-variants]');
      try {
        this.variants = JSON.parse(raw.textContent);
      } catch (e) {
        this.variants = [];
      }

      this.optionInputs = Array.from(this.querySelectorAll('[data-tf-option-input]'));
      this.quantityInput = this.querySelector('[data-tf-quantity]');
      this.submitButton = this.querySelector('[data-tf-submit]');
      this.submitText = this.querySelector('[data-tf-submit-text]');
      this.spinner = this.querySelector('.tf-submit__spinner');
      this.totalEl = this.querySelector('[data-tf-total]');
      this.breakdownEl = this.querySelector('[data-tf-breakdown]');
      this.errorEl = this.querySelector('[data-tf-error]');
      this.savingNote = this.querySelector('[data-tf-saving-note]');
      this.defaultLabel = (this.submitText.textContent || 'Add to cart').trim();

      // Each add-on block: input, its variant list, and the shopper's intent.
      this.addons = Array.from(this.querySelectorAll('[data-tf-addon-block]')).map((wrapper) => {
        const input = wrapper.querySelector('[data-tf-addon]');
        const script = wrapper.querySelector('[data-tf-addon-variants]');
        let variants = [];
        try {
          variants = JSON.parse(script.textContent);
        } catch (e) {
          variants = [];
        }

        // Intent is what the shopper asked for, kept separate from what is
        // currently possible. A tick survives a trip through a height that
        // has no plate in stock.
        input.dataset.tfIntent = input.checked ? 'true' : 'false';

        return {
          wrapper: wrapper,
          input: input,
          variants: variants,
          priceEl: wrapper.querySelector('[data-tf-addon-price]'),
          statusEl: wrapper.querySelector('[data-tf-addon-status]'),
          label: input.dataset.title || '',
          matchOption: input.dataset.matchOption || '',
          matchIndex: parseInt(input.dataset.matchIndex, 10),
          rackIndex: parseInt(input.dataset.rackOptionIndex, 10),
          savingEl: wrapper.querySelector('[data-tf-addon-saving]'),
          discountType: input.dataset.discountType || 'none',
          discountValue: parseFloat(input.dataset.discountValue) || 0
        };
      });

      this.quantityInput.addEventListener('input', () => this.refresh());
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-tf-addon]')) {
          event.target.dataset.tfIntent = event.target.checked ? 'true' : 'false';
        }
        if (event.target.matches('[data-tf-option-input], [data-tf-addon]')) this.refresh();
      });
      this.submitButton.addEventListener('click', () => this.submit());

      this.refresh();
    }

    /* --- rack state ----------------------------------------- */
    selectedOptions() {
      const values = [];
      this.querySelectorAll('[data-tf-option-index]').forEach((fieldset) => {
        const index = Number(fieldset.dataset.tfOptionIndex);
        const checked = fieldset.querySelector('[data-tf-option-input]:checked');
        values[index] = checked ? checked.value : null;
      });
      return values;
    }

    currentVariant() {
      const selected = this.selectedOptions();
      return this.variants.find((variant) =>
        variant.options.every((value, index) => value === selected[index])
      );
    }

    /* --- add-on resolution ---------------------------------- *
     * Returns { variant } or { error }. Never guesses: an add-on
     * that cannot be matched is disabled rather than priced off
     * the wrong variant.
     * -------------------------------------------------------- */
    resolveAddon(addon, selected) {
      if (!addon.variants.length) return { error: 'missing' };

      const matched = addon.matchOption !== '';

      if (matched && (addon.matchIndex < 0 || addon.rackIndex < 0)) {
        return { error: 'config' };
      }

      if (!matched) {
        const variant =
          addon.variants.find((v) => v.available) || addon.variants[0];
        return variant.available ? { variant: variant } : { error: 'soldout' };
      }

      const target = normalise(selected[addon.rackIndex]);
      const variant = addon.variants.find(
        (v) => normalise(v.options[addon.matchIndex]) === target
      );

      if (!variant) return { error: 'nomatch' };
      if (!variant.available) return { error: 'soldout' };
      return { variant: variant };
    }

    /* --- bundle saving (display only) ----------------------- *
     * Mirrors the native Buy X Get Y discount set up in admin.
     * Never sent to the cart; Shopify applies the real discount.
     * -------------------------------------------------------- */
    savingFor(addon, price) {
      if (!addon.discountValue || addon.discountValue <= 0) return 0;

      let saving = 0;
      if (addon.discountType === 'percent') {
        saving = Math.round((price * addon.discountValue) / 100);
      } else if (addon.discountType === 'amount') {
        saving = Math.round(addon.discountValue * 100);
      }

      return Math.max(0, Math.min(saving, price));
    }

    statusText(error, addon, selected) {
      switch (error) {
        case 'nomatch':
          return `Not available for ${selected[addon.rackIndex] || 'this size'}`;
        case 'soldout':
          return 'Out of stock';
        case 'config':
          return this.designMode
            ? `No option named "${addon.matchOption}" on both products`
            : 'Currently unavailable';
        case 'missing':
          return 'Currently unavailable';
        default:
          return 'Currently unavailable';
      }
    }

    /* --- render --------------------------------------------- */
    refresh() {
      const selected = this.selectedOptions();
      const variant = this.currentVariant();
      this.variant = variant;

      this.updateOptionAvailability();
      this.updateLegends();

      const lines = [];
      if (variant) lines.push({ title: variant.title, price: variant.price });

      let addonTotal = 0;
      let totalSaving = 0;
      this.resolved = [];

      this.addons.forEach((addon) => {
        const result = this.resolveAddon(addon, selected);

        if (result.error) {
          addon.input.disabled = true;
          addon.input.checked = false;
          addon.wrapper.classList.add('tf-addon-wrapper--blocked');
          addon.priceEl.textContent = '';
          if (addon.savingEl) addon.savingEl.hidden = true;
          addon.statusEl.hidden = false;
          addon.statusEl.textContent = this.statusText(result.error, addon, selected);

          if (result.error === 'config') {
            console.warn(
              `[TEN-FOUR] Add-on "${addon.label}" declares match option "${addon.matchOption}" ` +
                'but that option name was not found on both the rack and the add-on product.'
            );
          }
          return;
        }

        addon.input.disabled = false;
        addon.wrapper.classList.remove('tf-addon-wrapper--blocked');
        addon.statusEl.hidden = true;
        addon.statusEl.textContent = '';
        addon.input.checked = addon.input.dataset.tfIntent === 'true';
        addon.priceEl.textContent = `+ ${formatMoney(result.variant.price, this.moneyFormat)}`;

        const saving = this.savingFor(addon, result.variant.price);
        if (addon.savingEl) {
          addon.savingEl.hidden = saving <= 0;
          addon.savingEl.textContent =
            saving > 0 ? `Save ${formatMoney(saving, this.moneyFormat)} when bundled` : '';
        }

        if (!addon.input.checked) return;

        const suffix =
          addon.matchOption !== '' && result.variant.title !== 'Default Title'
            ? ` — ${result.variant.title}`
            : '';

        addonTotal += result.variant.price;
        totalSaving += saving;
        this.resolved.push({ variantId: result.variant.id });
        lines.push({ title: `${addon.label}${suffix}`, price: result.variant.price });
      });

      if (totalSaving > 0) {
        lines.push({ title: 'Bundle saving', price: totalSaving, saving: true });
      }

      this.breakdownEl.innerHTML = lines
        .map((line) => {
          const amount = formatMoney(line.price, this.moneyFormat);
          const modifier = line.saving ? ' tf-summary__line--saving' : '';
          const value = line.saving ? `\u2212${amount}` : amount;
          return `<li class="tf-summary__line${modifier}"><span>${line.title}</span><span>${value}</span></li>`;
        })
        .join('');

      if (this.savingNote) this.savingNote.hidden = totalSaving <= 0;

      const unit = (variant ? variant.price : 0) + addonTotal - totalSaving;
      const quantity = Math.max(1, parseInt(this.quantityInput.value, 10) || 1);
      this.totalEl.textContent = formatMoney(unit * quantity, this.moneyFormat);

      const sellable = variant && variant.available;
      this.submitButton.disabled = !sellable;
      this.submitText.textContent = variant
        ? sellable
          ? this.defaultLabel
          : 'Sold out'
        : 'Unavailable';

      if (variant && this.dataset.updateUrl === 'true' && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      this.setError(null);
    }

    updateOptionAvailability() {
      const selected = this.selectedOptions();
      this.querySelectorAll('[data-tf-option-index]').forEach((fieldset) => {
        const index = Number(fieldset.dataset.tfOptionIndex);
        fieldset.querySelectorAll('[data-tf-option-input]').forEach((input) => {
          const trial = selected.slice();
          trial[index] = input.value;
          const match = this.variants.find((variant) =>
            variant.options.every((value, i) => trial[i] == null || value === trial[i])
          );
          const label = fieldset.querySelector(`label[for="${input.id}"]`);
          const usable = Boolean(match && match.available);
          if (label) label.classList.toggle('tf-swatch--unavailable', !usable);
        });
      });
    }

    updateLegends() {
      const selected = this.selectedOptions();
      this.querySelectorAll('[data-tf-option-index]').forEach((fieldset) => {
        const index = Number(fieldset.dataset.tfOptionIndex);
        const out = fieldset.querySelector('[data-tf-option-current]');
        if (out) out.textContent = selected[index] || '';
      });
    }

    setError(message) {
      if (!this.errorEl) return;
      this.errorEl.hidden = !message;
      this.errorEl.textContent = message || '';
    }

    setBusy(busy) {
      this.submitButton.disabled = busy;
      this.submitButton.classList.toggle('tf-submit--busy', busy);
      if (this.spinner) this.spinner.hidden = !busy;
    }

    /* --- add to cart ---------------------------------------- */
    buildItems() {
      const quantity = Math.max(1, parseInt(this.quantityInput.value, 10) || 1);
      const items = [{ id: this.variant.id, quantity: quantity, properties: {} }];

      this.resolved.forEach((addon) => {
        items.push({ id: addon.variantId, quantity: quantity, properties: {} });
      });

      if (this.resolved.length) {
        // Deterministic key: identical configurations merge into one group.
        const key = [this.variant.id]
          .concat(this.resolved.map((a) => a.variantId).sort((a, b) => a - b))
          .join('-');
        items.forEach((item, index) => {
          item.properties._bundle = key;
          item.properties._bundle_role = index === 0 ? 'parent' : 'child';
        });
      }

      return items;
    }

    sectionsToRender() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return null;
      return [
        { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
      ];
    }

    async submit() {
      if (!this.variant || !this.variant.available) return;

      this.setBusy(true);
      this.setError(null);

      // Re-resolve so the submitted variant IDs match what is on screen now,
      // not what was on screen when the box was ticked.
      this.refresh();

      const sections = this.sectionsToRender();
      const body = { items: this.buildItems() };

      if (sections) {
        body.sections = sections.map((s) => s.section).join(',');
        body.sections_url = window.location.pathname;
      }

      try {
        const response = await fetch(`${CART_ADD}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        const data = await response.json();

        if (!response.ok) {
          this.setError(data.description || data.message || 'That could not be added to your cart.');
          return;
        }

        if (sections && data.sections) {
          this.renderSections(sections, data.sections);
          const drawer = document.querySelector('cart-drawer');
          if (drawer) {
            drawer.classList.remove('is-empty');
            if (typeof drawer.open === 'function') drawer.open();
          }
          document.dispatchEvent(new CustomEvent('tf:cart:updated'));
        } else {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
        }
      } catch (error) {
        this.setError('Network error. Try again.');
      } finally {
        this.setBusy(false);
      }
    }

    renderSections(config, payload) {
      const parser = new DOMParser();
      config.forEach((entry) => {
        const html = payload[entry.section];
        if (!html) return;
        const target = document.getElementById(entry.id);
        if (!target) return;
        const parsed = parser.parseFromString(html, 'text/html');
        const source = parsed.querySelector(entry.selector);
        const destination = target.querySelector(entry.selector) || target;
        if (source) destination.innerHTML = source.innerHTML;
      });
    }
  }

  if (!customElements.get('ten-four-builder')) {
    customElements.define('ten-four-builder', TenFourBuilder);
  }
})();
